import { useState, useRef } from "react";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isValidEmail } from "../../lib/validate";
import { CaptchaBox, captchaRequired } from "../CaptchaBox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterSignup = ({ compact = false }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [focused, setFocused] = useState(false);
  const capRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(email)) { toast.error("Enter a valid email, e.g. abc@gmail.com"); return; }
    if (captchaRequired() && !captcha) { toast.error("Please complete the security check first"); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, captcha_token: captcha || undefined }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Could not subscribe");
      setDone(true);
      toast.success("You're in — the weekly briefing lands every Wednesday.");
    } catch (err) { toast.error(err.message); capRef.current?.reset(); setCaptcha(null); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <p data-testid="newsletter-signup-done" className={`font-body text-sm text-gf-primary ${compact ? "" : "py-2"}`}>
        ✓ Subscribed — see you Wednesday.
      </p>
    );
  }

  const form = (
    <form onSubmit={submit} className="flex flex-col gap-2.5" data-testid="newsletter-signup-form">
      <div className="flex flex-col sm:flex-row gap-2.5">
        <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} onFocus={() => setFocused(true)}
          data-testid="newsletter-signup-email" placeholder="you@company.com"
          className="flex-1 min-w-0 bg-gf-bg border border-white/15 px-4 py-3 font-body text-sm text-gf-text placeholder:text-gf-muted/60 focus:outline-none focus:border-gf-primary transition-colors" />
        <button type="submit" disabled={busy} data-testid="newsletter-signup-submit"
          className="shrink-0 inline-flex items-center justify-center gap-2 bg-gf-primary text-gf-bg font-body font-bold text-sm px-5 py-3 hover:bg-gf-primary-hover transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Subscribe
        </button>
      </div>
      <CaptchaBox ref={capRef} onVerify={setCaptcha} active={focused || !!email} />
    </form>
  );

  if (compact) {
    return (
      <div data-testid="newsletter-signup-footer">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gf-muted mb-3">Weekly briefing</p>
        <p className="font-body text-sm text-gf-muted mb-3">Best blogs & tender results, every Wednesday.</p>
        {form}
      </div>
    );
  }

  return (
    <div className="bg-gf-surface border border-gf-primary/20 p-6 sm:p-8" data-testid="newsletter-signup-box">
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="lg:flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gf-primary">Get the weekly briefing</p>
          <p className="font-body text-sm text-gf-muted mt-2">
            The week's best expert blogs and live tender results — one email, every Wednesday. No spam.
          </p>
        </div>
        <div className="lg:w-[420px]">{form}</div>
      </div>
    </div>
  );
};
